import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  ArrowLeft,
  Calendar,
  CalendarClock,
  ClipboardList,
  Clock,
  ExternalLink,
  Layers,
  ChevronDown,
  ChevronUp,
  Info,
  Radio,
  Trophy,
  Users,
} from 'lucide-react';
import {
  getMyGroup,
  listMyBuddyGroups,
  getBuddyGroupActiveRegistrations,
  type BuddyGroupActiveRegistration,
} from '@/services/buddy-group.service';
import { WithdrawBuddyGroupModal } from '@/components/buddy-groups/WithdrawBuddyGroupModal';
import type { BuddyGroupDetail, BuddyGroupSummary } from '@/types/buddy-groups';
import { WATCH_LIVE_ENABLED } from '@/constants/feature-flags';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { EmptyState } from '@/components/ui/empty-state';
import { HelpBanner } from '@/components/ui/help-banner';
import { SectionLabel } from '@/components/ui/section-label';
import { EventCard } from '@/components/events/EventCard';
import { useEventsPrizeTotal, type PrizeEventLike } from '@/api/extensions/usePrizeOps';
import { DaySheetView } from '@/components/events/DaySheetView';
import { useDismissibleHelp } from '@/hooks/useDismissibleHelp';
import { useRodeoDetail } from '@/api/extensions/useRodeoOps';
import { useMyRegistrations } from '@/api/extensions/useRegistrationOps';
import { useOrganizations } from '@/hooks/useOrganizations';
import { useUserHandicaps } from '@/hooks/useUserHandicaps';
import { useAuthStore } from '@/state_management_and_stores/auth.store';
import { useBreadcrumb } from '@/contexts/BreadcrumbContext';
import { formatCurrency, formatDateRange } from '@/utils/formatters';
import { formatInTimezone } from '@/utils/timezone';
import { formatPreferenceLabel } from '@/utils/format';
import { getEventDisplayTitle } from '@/utils/eventTypes';
import { PerformancesClient } from '@/api/generated_clients/generated-performances-api-client';
import type { PerformancesRow } from '@/types/generated_schemas/generated-performances-schema';

export default function RodeoDetailPage() {
  const { rodeoId } = useParams<{ rodeoId: string }>();
  const navigate = useNavigate();
  const [infoOpen, setInfoOpen] = useState(false);

  const { data: rodeo, isLoading, isError } = useRodeoDetail(rodeoId!);
  const { data: myRegistrations } = useMyRegistrations();
  const { data: userHandicaps } = useUserHandicaps();
  const buddyGroupHelp = useDismissibleHelp('buddy_group_intro');
  // Resolve primary_organization_id → name on the client. The /jackpot-ops/:id
  // backend selects rodeos.* without joining organizations, so we hydrate the
  // host org's display name here for the hero.
  const { data: myOrganizations } = useOrganizations();
  const userProfileId = useAuthStore((s) => s.profile?.id ?? null);
  const { setTrail } = useBreadcrumb();

  // Fetch the rodeo's performances + slacks so we can populate the
  // schedule column in the overview and the "Performances" stat card.
  // Same generated client / unauthenticated endpoint the EventDetailPage
  // uses, just scoped to this rodeo via `trig_rodeo_id`.
  const { data: rodeoPerformances } = useQuery<PerformancesRow[]>({
    queryKey: ['rodeo-performances', rodeoId],
    queryFn: () =>
      new PerformancesClient().getAll({
        filters: { trig_rodeo_id: rodeoId },
        sortBy: 'start_datetime',
        sortOrder: 'asc',
        limit: 200,
      }),
    enabled: !!rodeoId,
  });

  // Set breadcrumb trail: Rodeos > [Rodeo Name]
  useEffect(() => {
    if (rodeo?.name) {
      setTrail([
        { label: 'Rodeos', path: '/rodeos' },
        { label: rodeo.name, path: `/rodeos/${rodeoId}` },
      ]);
    }
    return () => setTrail(null);
  }, [rodeoId, rodeo?.name, setTrail]);

  // Buddy-group state for the rodeo card. We resolve four states:
  //   • registered_primary  — user is in a group registered for this rodeo,
  //     and they are the primary cardholder (can manage / unregister).
  //   • registered_member   — user is a non-primary member of the group
  //     registered for this rodeo (can only view / leave the group).
  //   • can_register        — user has at least one buddy group of which
  //     they are the primary that isn't tied to a rodeo yet, so they can
  //     register one of those for this rodeo (or create another).
  //   • must_create         — user has no eligible primary-of group; they
  //     either need to create one, or ask their primary cardholder to
  //     register the group they're in.
  const [myGroup, setMyGroup] = useState<BuddyGroupDetail | null>(null);
  const [otherGroups, setOtherGroups] = useState<BuddyGroupSummary[]>([]);
  const [bgLoading, setBgLoading] = useState(true);
  const [withdrawOpen, setWithdrawOpen] = useState(false);
  const [buddyGroupRegistrations, setBuddyGroupRegistrations] = useState<
    BuddyGroupActiveRegistration[]
  >([]);

  const refreshBuddyGroupState = async () => {
    if (!rodeoId || !userProfileId) return;
    setBgLoading(true);
    try {
      const grp = await getMyGroup(rodeoId).catch(() => null);
      setMyGroup(grp);
      // Always pull the user's full list — we use it for "can register"
      // detection and to indicate "you're in a group but not primary".
      const all = await listMyBuddyGroups(userProfileId).catch(
        () => [] as BuddyGroupSummary[],
      );
      setOtherGroups(all);
      if (grp?.id) {
        const regs = await getBuddyGroupActiveRegistrations(grp.id).catch(
          () => [] as BuddyGroupActiveRegistration[],
        );
        setBuddyGroupRegistrations(regs);
      } else {
        setBuddyGroupRegistrations([]);
      }
    } finally {
      setBgLoading(false);
    }
  };

  useEffect(() => {
    if (!rodeoId || !userProfileId) return;
    let cancelled = false;
    (async () => {
      const grp = await getMyGroup(rodeoId).catch(() => null);
      if (cancelled) return;
      setMyGroup(grp);
      const all = await listMyBuddyGroups(userProfileId).catch(
        () => [] as BuddyGroupSummary[],
      );
      if (cancelled) return;
      setOtherGroups(all);
      if (grp?.id) {
        const regs = await getBuddyGroupActiveRegistrations(grp.id).catch(
          () => [] as BuddyGroupActiveRegistration[],
        );
        if (cancelled) return;
        setBuddyGroupRegistrations(regs);
      } else {
        setBuddyGroupRegistrations([]);
      }
      setBgLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [rodeoId, userProfileId]);

  const isPrimaryOfGroup =
    !!myGroup && !!userProfileId && myGroup.primary_member_user_profile_id === userProfileId;

  // Eligible groups for "register": every primary, non-disbanded group
  // the user owns. Buddy groups are M:N with rodeos — the same group can
  // be registered for any number of rodeos at once — so we don't filter
  // out groups attached to OTHER rodeos. The setup page itself handles
  // the per-row "already registered for this rodeo" case via
  // attached_rodeo_ids.
  const eligibleForRegister = otherGroups.filter(
    (g) => g.is_primary && g.status !== 'disbanded',
  );
  // Groups the user is in but where they are NOT the primary — used for
  // the "ask your primary" hint in the must_create state.
  const memberOnlyGroups = otherGroups.filter(
    (g) => !g.is_primary && g.status !== 'disbanded',
  );

  const buddyGroupCardState: 'registered_primary' | 'registered_member' | 'can_register' | 'must_create' =
    myGroup
      ? isPrimaryOfGroup
        ? 'registered_primary'
        : 'registered_member'
      : eligibleForRegister.length > 0
        ? 'can_register'
        : 'must_create';

  // Inline button to re-open the buddy-group help banner once dismissed. Sits
  // next to the card's title text in every state so the user always has a way
  // back to the explanation. Hidden when the banner itself is showing.
  const buddyGroupHelpReopen = buddyGroupHelp.dismissed ? (
    <button
      type="button"
      onClick={buddyGroupHelp.reopen}
      aria-label="Show buddy group help"
      className="rounded-md p-0.5 text-muted-foreground hover:text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
    >
      <Info className="h-3.5 w-3.5" />
    </button>
  ) : null;

  // Withdrawal goes through WithdrawBuddyGroupModal so the captain sees
  // the impact preview (how many entries become solo entries, and that
  // none of them are cancelled) before committing.
  const handleUnregister = () => {
    if (!myGroup) return;
    setWithdrawOpen(true);
  };

  // Build a map of event_id → registration. Skip cancelled rows so the
  // EventCard "registered" highlight doesn't survive an unregister →
  // refresh — `useMyRegistrations()` returns cancelled rows alongside
  // active ones, and the card's status field on the row is
  // `trig_status_name` (not `status`), which the card alone can't
  // reliably distinguish.
  const registrationMap = new Map<string, any>();
  if (myRegistrations) {
    for (const reg of myRegistrations) {
      const status = (reg as any).trig_status_name ?? (reg as any).status;
      if (status === 'cancelled') continue;
      registrationMap.set(reg.event_id, reg);
    }
  }

  // Build a map of event_id → list of buddy group members registered for
  // that event (self + teammates). Drives the avatar-bubble indicators on
  // each event card so the user can see at-a-glance which events the
  // group has filled out. Deduplicated per event_id × user_profile_id.
  const buddyGroupEventMap = new Map<
    string,
    Array<{ user_profile_id: string; display_name: string | null; is_self: boolean }>
  >();
  if (myGroup && buddyGroupRegistrations.length > 0) {
    const memberById = new Map<string, string | null>();
    for (const m of myGroup.members ?? []) {
      memberById.set(m.user_profile_id, m.display_name);
    }
    for (const reg of buddyGroupRegistrations) {
      if (!reg.event_id || !reg.user_profile_id) continue;
      const list = buddyGroupEventMap.get(reg.event_id) ?? [];
      if (list.some((e) => e.user_profile_id === reg.user_profile_id)) continue;
      list.push({
        user_profile_id: reg.user_profile_id,
        display_name:
          memberById.get(reg.user_profile_id) ??
          (reg.user_profile_id === userProfileId ? 'You' : null),
        is_self: reg.user_profile_id === userProfileId,
      });
      buddyGroupEventMap.set(reg.event_id, list);
    }
  }

  if (isLoading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-8 w-48" />
        <Skeleton className="h-32 w-full" />
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {Array.from({ length: 3 }).map((_, i) => (
            <Skeleton key={i} className="h-40" />
          ))}
        </div>
      </div>
    );
  }

  if (isError || !rodeo) {
    return (
      <div className="space-y-4">
        <Button variant="ghost" size="sm" onClick={() => navigate('/rodeos')}>
          <ArrowLeft className="h-4 w-4 mr-1" /> Back to Rodeos
        </Button>
        <EmptyState
          icon={Calendar}
          title="Rodeo not found"
          description="This rodeo may have been removed or is not published."
          action={{ label: 'Browse Rodeos', onClick: () => navigate('/rodeos') }}
        />
      </div>
    );
  }

  // Redirect if not a rodeo
  if ((rodeo as any).rodeo_or_jackpot && (rodeo as any).rodeo_or_jackpot !== 'rodeo') {
    navigate(`/jackpots/${rodeoId}`, { replace: true });
    return null;
  }

  // Alphabetise events by display title so the grid scans predictably.
  // `getEventDisplayTitle` already returns the custom name if present and
  // falls back to the event type, matching what the cards render.
  const events: any[] = [...((rodeo as any).events ?? [])].sort((a, b) =>
    getEventDisplayTitle(a).localeCompare(getEventDisplayTitle(b), undefined, {
      sensitivity: 'base',
    }),
  );
  const description = (rodeo as any).description ?? '';
  const r = rodeo as any;

  // Sort client-side by start_datetime ascending so the schedule reads
  // top-to-bottom regardless of how the API returns rows; rows without a
  // start_datetime sink to the bottom rather than topping the list.
  const performances = [...(rodeoPerformances ?? [])].sort((a, b) => {
    const ta = a.start_datetime ? new Date(a.start_datetime).getTime() : Infinity;
    const tb = b.start_datetime ? new Date(b.start_datetime).getTime() : Infinity;
    return ta - tb;
  });

  // Pin every wall-clock display to the rodeo's saved timezone so admins
  // and contestants in different zones see the same time the host
  // entered. Falls back to Pacific so legacy rows with a NULL column
  // still render — the DB default is the same value.
  const rodeoTimezone =
    (typeof r.timezone === 'string' && r.timezone) ||
    'America/Los_Angeles';
  const entryCloseDateLabel = r.entry_close_time
    ? formatInTimezone(
        r.entry_close_time,
        rodeoTimezone,
        { month: 'short', day: 'numeric', year: 'numeric' },
        false,
      )
    : null;
  const entryCloseLabel = r.entry_close_time
    ? formatInTimezone(r.entry_close_time, rodeoTimezone, {
        dateStyle: 'medium',
        timeStyle: 'short',
      })
    : null;

  const dateRangeLabel = formatDateRange(r.start_date, r.end_date);

  // Resolve the host organization name from primary_organization_id. The
  // detail endpoint doesn't join organizations, so we look it up against
  // the user's organization memberships. Falls back to a pre-hydrated
  // organization_name if some upstream caller already resolved it.
  const hostOrgName: string | null = (() => {
    if (typeof r.organization_name === 'string' && r.organization_name) {
      return r.organization_name;
    }
    const orgId = r.primary_organization_id;
    if (typeof orgId !== 'string' || !orgId) return null;
    const match = (myOrganizations ?? []).find(
      (o: any) => o?.id === orgId,
    ) as { name?: string } | undefined;
    return match?.name ?? null;
  })();

  // Whether to show the Prize Pool stat at all (cheap, no hook): any event
  // with a static prize, or a dynamic event that shows its prize. The actual
  // (live) total is computed inside RodeoPrizePoolStatCard via a hook, which
  // can't run here because of the early returns above.
  const hasPrize = events.some(
    (ev: any) =>
      !!ev?.display_prize_to_contestants &&
      (ev?.prize_allocation_mode === 'dynamic' || Number(ev?.total_prize_amount) > 0),
  );

  return (
    <div className="space-y-6 max-w-5xl mx-auto">
      {/* Back button */}
      <Button variant="ghost" size="sm" onClick={() => navigate('/rodeos')}>
        <ArrowLeft className="h-4 w-4 mr-1" /> Back to Rodeos
      </Button>

      {/* ─── Hero Header ──────────────────────────────── */}
      <div className="space-y-2">
        {/* Host organization eyebrow — surfaced above the title so attendees
            immediately see which org is putting on this rodeo. Hidden when
            the org can't be resolved (rare, e.g. legacy rows without a
            primary_organization_id) rather than showing a noisy fallback. */}
        {hostOrgName && (
          <p className="text-sm font-medium text-muted-foreground">
            {hostOrgName}
          </p>
        )}
        <div className="flex items-center gap-3 flex-wrap">
          <h1 className="text-2xl font-bold tracking-tight">{rodeo.name}</h1>
          <Badge variant="secondary">
            {events.length} event{events.length !== 1 ? 's' : ''}
          </Badge>
          {r.is_published === false && (
            <Badge variant="outline">Draft</Badge>
          )}
          {WATCH_LIVE_ENABLED && (
            <Button
              variant="outline"
              size="sm"
              className="ml-auto"
              onClick={() => navigate(`/rodeos/${rodeoId}/live`)}
            >
              <Radio className="h-4 w-4 mr-1 text-red-500" /> Watch Live
            </Button>
          )}
        </div>
      </div>

      {/* ─── Quick Stats ──────────────────────────────── */}
      {(() => {
        const stats: {
          icon: React.ComponentType<{ className?: string }>;
          label: string;
          value: string;
        }[] = [];
        if (dateRangeLabel) {
          stats.push({ icon: Calendar, label: 'Dates', value: dateRangeLabel });
        }
        if (entryCloseDateLabel) {
          stats.push({ icon: CalendarClock, label: 'Entries Close', value: entryCloseDateLabel });
        }
        const slotCount = stats.length + (hasPrize ? 1 : 0);
        if (slotCount === 0) return null;
        return (
          <div
            className={`grid grid-cols-2 ${slotCount >= 4 ? 'sm:grid-cols-4' : slotCount === 3 ? 'sm:grid-cols-3' : 'sm:grid-cols-2'} gap-3`}
          >
            {stats.map((s) => (
              <StatCard key={s.label} icon={s.icon} label={s.label} value={s.value} />
            ))}
            {hasPrize && <RodeoPrizePoolStatCard events={events as PrizeEventLike[]} />}
          </div>
        );
      })()}

      {/* ─── Overview ──────────────────────────────── */}
      <Card>
        <CardContent className="p-5">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-3">
            {/* Schedule */}
            <div className="space-y-2">
              <SectionLabel
                icon={<Calendar className="h-3.5 w-3.5" />}
                color="muted"
              >
                Schedule
              </SectionLabel>
              {dateRangeLabel && <DetailRow label="Dates" value={dateRangeLabel} />}
              {entryCloseLabel && (
                <DetailRow label="Entries Close" value={entryCloseLabel} />
              )}
              {r.slack_time && (
                <DetailRow label="Slack Time" value={r.slack_time} />
              )}
              {r.fees_due_message && (
                <p className="pt-1 text-xs text-muted-foreground italic">
                  {r.fees_due_message}
                </p>
              )}
            </div>

            {/* Setup */}
            <div className="space-y-2">
              <SectionLabel
                icon={<Info className="h-3.5 w-3.5" />}
                color="muted"
              >
                Setup
              </SectionLabel>
              {(() => {
                // Rodeo-level view: Pay Later is a rodeo switch (individual
                // events may still override it off). `allow_pay_later` defaults
                // on at the column, so absent → listed.
                const methods = ['Card', 'ACH'];
                if ((r as any).allow_pay_later !== false) methods.push('Pay Later');
                if (r.allow_cash_payment) methods.push('Cash');
                return (
                  <DetailRow
                    label="Payment Options"
                    value={methods.join(', ')}
                  />
                );
              })()}
              {Number(r.non_member_surcharge) > 0 && (
                <DetailRow
                  label="Non-Member Surcharge"
                  value={formatCurrency(r.non_member_surcharge)}
                />
              )}
              {r.url && (
                <a
                  href={r.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-1.5 text-xs text-primary hover:underline"
                >
                  <ExternalLink className="h-3 w-3" /> Rodeo website
                </a>
              )}
            </div>

            {/* Performances & Slacks */}
            {performances.length > 0 && (
              <div className="space-y-2">
                <SectionLabel
                  icon={<Clock className="h-3.5 w-3.5" />}
                  color="muted"
                >
                  Performances &amp; Slacks
                </SectionLabel>
                <ul className="text-sm space-y-1">
                  {performances.map((p) => {
                    const dt = p.start_datetime
                      ? formatInTimezone(p.start_datetime, rodeoTimezone, {
                          dateStyle: 'medium',
                          timeStyle: 'short',
                        })
                      : null;
                    return (
                      <li
                        key={p.id}
                        className="flex items-start justify-between gap-3"
                      >
                        <span className="font-medium flex items-center gap-1.5">
                          {p.performance_name ||
                            (p.is_slack ? 'Slack' : 'Performance')}
                          {p.is_slack && (
                            <Badge
                              variant="outline"
                              className="text-[10px] py-0 px-1.5 h-4"
                            >
                              Slack
                            </Badge>
                          )}
                        </span>
                        {dt && (
                          <span className="text-xs text-muted-foreground whitespace-nowrap">
                            {dt}
                          </span>
                        )}
                      </li>
                    );
                  })}
                </ul>
              </div>
            )}
          </div>

          {description && (
            <div className="mt-5 pt-4 border-t">
              <button
                className="flex items-center gap-1 text-sm font-medium text-primary"
                onClick={() => setInfoOpen(!infoOpen)}
              >
                {infoOpen ? 'Hide' : 'Show'} description
                {infoOpen ? (
                  <ChevronUp className="h-3.5 w-3.5" />
                ) : (
                  <ChevronDown className="h-3.5 w-3.5" />
                )}
              </button>
              {infoOpen && (
                <p className="mt-2 text-sm text-muted-foreground whitespace-pre-wrap">
                  {description}
                </p>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Buddy Group entry point — only when the rodeo allows them.
          Violet accent stripe matches the dashboard and Event Detail
          buddy-group treatment so the section reads as the same
          concept across the app. */}
      {(rodeo as any).buddy_groups_enabled &&
        (rodeo as any).is_finals !== true &&
        !bgLoading && (
        <Card className="relative border-2 border-violet-300 dark:border-violet-800 overflow-hidden">
          <span
            aria-hidden
            className="absolute left-0 top-0 bottom-0 w-1 bg-violet-500/70"
          />
          <CardContent className="p-4 pl-5 space-y-4">
            {!buddyGroupHelp.dismissed && (
              <HelpBanner
                title="What's a buddy group?"
                onDismiss={buddyGroupHelp.dismiss}
              >
                <p>
                  A <strong>buddy group</strong> is a small set of contestants —
                  usually friends or family traveling together — who want to be
                  scheduled for the same performance or slack so they can compete
                  on the same day.
                </p>
                <ul className="list-disc pl-5 space-y-1 mt-2">
                  <li>
                    The group has a <strong>primary cardholder</strong> who creates
                    the group and invites members. Invitations must be accepted
                    before a member counts as part of the group.
                  </li>
                  <li>
                    Once a group is registered for a rodeo, the primary picks the
                    group&apos;s <strong>1st &amp; 2nd performance preferences</strong>{' '}
                    and the host does their best to honor them for everyone in
                    the group.
                  </li>
                  <li>
                    The primary can register everyone for events at once, or leave
                    members to enter individually — either way they stay tied to the
                    group&apos;s schedule preferences.
                  </li>
                  <li>
                    A contestant can only be in <strong>one buddy group per rodeo</strong>.
                    To switch groups for this rodeo, leave the current one first.
                  </li>
                </ul>
              </HelpBanner>
            )}
            <div className="flex items-center justify-between gap-3 flex-wrap">
            <div className="flex items-center gap-3 text-sm">
              <Users className="h-4 w-4 text-muted-foreground shrink-0" />
              <div>
                {buddyGroupCardState === 'registered_primary' && myGroup && (
                  <>
                    <p className="font-medium flex items-center gap-1.5">
                      <span>Registered: {myGroup.group_name ?? 'Unnamed group'}</span>
                      {buddyGroupHelpReopen}
                    </p>
                    <p className="text-muted-foreground text-xs">
                      You&apos;re the primary cardholder ·{' '}
                      {myGroup.preferences_locked_at
                        ? `Preferences: ${formatPreferenceLabel(myGroup.preferences_first, rodeoTimezone) || '—'}${
                            myGroup.preferences_second
                              ? ` → ${formatPreferenceLabel(myGroup.preferences_second, rodeoTimezone)}`
                              : ''
                          }`
                        : 'Preferences not yet set'}
                    </p>
                  </>
                )}
                {buddyGroupCardState === 'registered_member' && myGroup && (
                  <>
                    <p className="font-medium flex items-center gap-1.5">
                      <span>Registered with: {myGroup.group_name ?? 'Unnamed group'}</span>
                      {buddyGroupHelpReopen}
                    </p>
                    <p className="text-muted-foreground text-xs">
                      You&apos;re a member of this group. To unregister, leave
                      the group from the buddy group page.
                    </p>
                  </>
                )}
                {buddyGroupCardState === 'can_register' && (
                  <>
                    <p className="font-medium flex items-center gap-1.5">
                      <span>No buddy group entered yet</span>
                      {buddyGroupHelpReopen}
                    </p>
                    <p className="text-muted-foreground text-xs">
                      You have {eligibleForRegister.length} eligible buddy group
                      {eligibleForRegister.length === 1 ? '' : 's'}. Enter
                      one for this rodeo to lock 1st &amp; 2nd performance
                      preferences for everyone in your group.
                    </p>
                  </>
                )}
                {buddyGroupCardState === 'must_create' && (
                  <>
                    <p className="font-medium flex items-center gap-1.5">
                      <span>No buddy group entered yet</span>
                      {buddyGroupHelpReopen}
                    </p>
                    <p className="text-muted-foreground text-xs">
                      {memberOnlyGroups.length > 0
                        ? "You're in a buddy group but you aren't the primary cardholder. Ask your primary to enter the group for this rodeo, or create your own."
                        : 'Create a buddy group to enter it for this rodeo and lock 1st & 2nd performance preferences for everyone.'}
                    </p>
                  </>
                )}
              </div>
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              {buddyGroupCardState === 'registered_primary' && (
                <>
                  <Button
                    size="sm"
                    onClick={() =>
                      navigate(`/rodeos/${rodeoId}/buddy-group/setup`)
                    }
                  >
                    Manage
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={handleUnregister}
                  >
                    Withdraw
                  </Button>
                </>
              )}
              {buddyGroupCardState === 'registered_member' && myGroup && (
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => navigate(`/my/buddy-groups/${myGroup.id}`)}
                >
                  View buddy group
                </Button>
              )}
              {buddyGroupCardState === 'can_register' && (
                <Button
                  size="sm"
                  onClick={() => navigate(`/rodeos/${rodeoId}/buddy-group/setup`)}
                >
                  Register buddy group
                </Button>
              )}
              {buddyGroupCardState === 'must_create' && (
                <Button
                  size="sm"
                  onClick={() => navigate(`/rodeos/${rodeoId}/buddy-group/setup`)}
                >
                  Create buddy group
                </Button>
              )}
            </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Events list (tabs for Day Sheets / Results / Priority will be
          re-introduced later; events-only for now). */}
      <div className="space-y-4">
        <SectionLabel
          icon={<Layers className="h-4 w-4" />}
          color="primary"
          trailing={
            <span className="text-xs text-muted-foreground">
              ({events.length})
            </span>
          }
        >
          Events
        </SectionLabel>
        {events.length === 0 ? (
          <EmptyState
            icon={Calendar}
            title="No events yet"
            description="Events for this rodeo haven't been published yet."
          />
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {events.map((event: any) => (
              <EventCard
                key={event.id}
                event={event}
                registrationStatus={registrationMap.get(event.id) ?? null}
                userHandicaps={userHandicaps ?? []}
                buddyGroupRegistrations={buddyGroupEventMap.get(event.id)}
              />
            ))}
          </div>
        )}
      </div>

      {events.length > 0 && (
        <div className="space-y-4">
          <SectionLabel icon={<ClipboardList className="h-4 w-4" />} color="primary">
            Day Sheets
          </SectionLabel>
          <DaySheetView events={events} />
        </div>
      )}

      {myGroup && rodeoId && (
        <WithdrawBuddyGroupModal
          open={withdrawOpen}
          onOpenChange={setWithdrawOpen}
          groupId={myGroup.id}
          groupName={myGroup.group_name ?? null}
          rodeoId={rodeoId}
          onWithdrawn={() => {
            void refreshBuddyGroupState();
          }}
        />
      )}
    </div>
  );
}

/* ─── Prize Pool stat — live total across the rodeo's events ─────────────── */
function RodeoPrizePoolStatCard({ events }: { events: PrizeEventLike[] }) {
  const total = useEventsPrizeTotal(events);
  return <StatCard icon={Trophy} label="Prize Pool" value={formatCurrency(total)} />;
}

/* ─── Stat Card Component ──────────────────────────────── */
function StatCard({
  icon: Icon,
  label,
  value,
}: {
  icon: React.ComponentType<{ className?: string }>;
  label: string;
  value: string;
}) {
  return (
    <div className="rounded-xl border border-primary/20 bg-primary/5 p-4 space-y-2">
      <div className="flex items-center gap-1.5">
        <Icon className="h-4 w-4 text-primary" />
        <span className="text-xs text-muted-foreground">{label}</span>
      </div>
      <p className="text-2xl font-bold tracking-tight text-center">{value}</p>
    </div>
  );
}

/* ─── Detail Row Component ──────────────────────────────── */
function DetailRow({
  label,
  value,
}: {
  label: string;
  value: string | number | null | undefined;
}) {
  if (value == null || value === '' || value === '—') return null;
  return (
    <div className="flex items-center justify-between text-sm gap-3">
      <span className="text-muted-foreground">{label}</span>
      <span className="font-medium text-right">{value}</span>
    </div>
  );
}
